import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { ROLES, parsePeriod } from '../../../constants';
import { adminService } from '../../../services/adminService';
import { extractList, extractMeta } from '../../../services/api';
import Loading from '../../../shared/Loading';
import EmptyState from '../../../shared/EmptyState';

const PERIOD_REGEX = /^\d{6}$/;
const MONTH_INPUT_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;

const MISSING_REASON_LABELS = {
  report_not_found: 'Report Belum Dibuat',
  report_detail_not_found: 'Detail Report Belum Ada',
};

const getCurrentPeriod = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}${month}`;
};

const normalizePeriod = (value) => {
  const raw = String(value || '').trim();
  return PERIOD_REGEX.test(raw) ? raw : '';
};

const periodToMonthInput = (period) => {
  const normalized = normalizePeriod(period);
  if (!normalized) return '';
  return `${normalized.slice(0, 4)}-${normalized.slice(4, 6)}`;
};

const monthInputToPeriod = (value) => {
  const raw = String(value || '').trim();
  if (!MONTH_INPUT_REGEX.test(raw)) return '';
  return raw.replace('-', '');
};

const normalizeTextFilter = (value) => String(value || '').trim();

const normalizePage = (value, fallback = 1) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return fallback;
  return parsed;
};

const normalizeCompanyFilter = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1) return '';
  return String(parsed);
};

const normalizeCompanyId = (value) => {
  const normalized = normalizeCompanyFilter(value);
  if (!normalized) return undefined;
  return Number(normalized);
};

const getDisplayWilayah = (wilayah) => {
  if (typeof wilayah !== 'string') return '-';
  const trimmed = wilayah.trim();
  return trimmed || '-';
};

const resolveMissingReasonLabel = (value) => {
  const normalized = String(value || '').trim();
  return MISSING_REASON_LABELS[normalized] || normalized || '-';
};

const getPeriodLabel = (period) => {
  const normalized = normalizePeriod(period);
  if (!normalized) return '-';
  const { month, year, label } = parsePeriod(normalized);
  if (!Number.isFinite(month) || !Number.isFinite(year) || !label) return normalized;
  return label;
};

const MissingReportsPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuperadmin = user?.role === ROLES.SUPERADMIN;

  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const hasLoadedOnceRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState('');

  const periodFilter = normalizePeriod(searchParams.get('period')) || getCurrentPeriod();
  const wilayahFilter = normalizeTextFilter(searchParams.get('wilayah'));
  const searchFilter = normalizeTextFilter(searchParams.get('search'));
  const companyFilter = isSuperadmin ? normalizeCompanyFilter(searchParams.get('company_id')) : '';
  const listPage = normalizePage(searchParams.get('page'));

  useEffect(() => {
    if (!isSuperadmin) return;
    adminService.getCompanies({ page: 1, limit: 1000 }).then((res) => {
      setCompanies(extractList(res.data));
    }).catch(() => {
      setCompanies([]);
    });
  }, [isSuperadmin]);

  const fetchMissingEmployees = useCallback(async ({
    page = 1,
    period = getCurrentPeriod(),
    wilayah = '',
    search = '',
    company = '',
  } = {}) => {
    if (!hasLoadedOnceRef.current) setLoading(true);
    setErrorMessage('');

    try {
      const res = await adminService.getMissingEmployees({
        page,
        limit: 10,
        period,
        wilayah: wilayah || undefined,
        search: search || undefined,
        company_id: isSuperadmin ? normalizeCompanyId(company) : undefined,
      });

      setItems(extractList(res.data));
      setPagination(extractMeta(res.data));
    } catch (error) {
      setItems([]);
      setPagination({ page: 1, last_page: 1, total: 0 });

      if (error?.httpStatus === 422) {
        setErrorMessage(error?.message || 'Period wajib diisi (format YYYYMM)');
      } else if (error?.httpStatus === 403) {
        setErrorMessage(error?.message || 'Anda tidak memiliki akses ke company ini');
      } else {
        setErrorMessage(error?.message || 'Gagal memuat missing reports');
      }
    } finally {
      setLoading(false);
      hasLoadedOnceRef.current = true;
    }
  }, [isSuperadmin]);

  useEffect(() => {
    fetchMissingEmployees({
      page: listPage,
      period: periodFilter,
      wilayah: wilayahFilter,
      search: searchFilter,
      company: companyFilter,
    });
  }, [fetchMissingEmployees, listPage, periodFilter, wilayahFilter, searchFilter, companyFilter]);

  const updateListSearchParams = ({
    periodValue = periodFilter,
    wilayahValue = wilayahFilter,
    searchValue = searchFilter,
    companyValue = companyFilter,
    pageValue = listPage,
  } = {}) => {
    const next = new URLSearchParams(searchParams);

    const normalizedPeriod = normalizePeriod(periodValue) || getCurrentPeriod();
    next.set('period', normalizedPeriod);

    const normalizedWilayah = normalizeTextFilter(wilayahValue);
    if (normalizedWilayah) next.set('wilayah', normalizedWilayah);
    else next.delete('wilayah');

    const normalizedSearch = normalizeTextFilter(searchValue);
    if (normalizedSearch) next.set('search', normalizedSearch);
    else next.delete('search');

    if (isSuperadmin) {
      const normalizedCompany = normalizeCompanyFilter(companyValue);
      if (normalizedCompany) next.set('company_id', normalizedCompany);
      else next.delete('company_id');
    } else {
      next.delete('company_id');
    }

    const normalizedPage = normalizePage(pageValue);
    if (normalizedPage > 1) next.set('page', String(normalizedPage));
    else next.delete('page');

    setSearchParams(next);
  };

  const handlePeriodChange = (event) => {
    updateListSearchParams({
      periodValue: monthInputToPeriod(event.target.value) || getCurrentPeriod(),
      pageValue: 1,
    });
  };

  const handleWilayahChange = (event) => {
    updateListSearchParams({
      wilayahValue: event.target.value,
      pageValue: 1,
    });
  };

  const handleSearchChange = (event) => {
    updateListSearchParams({
      searchValue: event.target.value,
      pageValue: 1,
    });
  };

  const handleCompanyChange = (event) => {
    updateListSearchParams({
      companyValue: event.target.value,
      pageValue: 1,
    });
  };

  const handleResetFilters = () => {
    updateListSearchParams({
      periodValue: getCurrentPeriod(),
      wilayahValue: '',
      searchValue: '',
      companyValue: '',
      pageValue: 1,
    });
  };

  const selectedCompanyLabel = companyFilter
    ? (companies.find((company) => String(company.company_id) === String(companyFilter))?.name || companyFilter)
    : '';

  const selectedFilters = useMemo(() => [
    `Periode ${getPeriodLabel(periodFilter)}`,
    wilayahFilter ? `Wilayah ${wilayahFilter}` : '',
    searchFilter ? `Search "${searchFilter}"` : '',
    isSuperadmin && selectedCompanyLabel ? `Company ${selectedCompanyLabel}` : '',
  ].filter(Boolean), [periodFilter, wilayahFilter, searchFilter, isSuperadmin, selectedCompanyLabel]);

  if (loading && !hasLoadedOnceRef.current) return <Loading />;

  return (
    <div>
      {errorMessage && (
        <div className="dash-inline-state dash-inline-state--error">
          {errorMessage}
        </div>
      )}

      <div className="admin-users-toolbar">
        <div className="admin-table-controls">
          <label htmlFor="missing-reports-period-filter">Periode</label>
          <input
            id="missing-reports-period-filter"
            type="month"
            className="admin-filter-select"
            value={periodToMonthInput(periodFilter)}
            onChange={handlePeriodChange}
          />

          <label htmlFor="missing-reports-wilayah-filter">Wilayah</label>
          <input
            id="missing-reports-wilayah-filter"
            type="text"
            className="admin-filter-select"
            value={wilayahFilter}
            onChange={handleWilayahChange}
            placeholder="Contoh: JABODETABEK"
          />

          <label htmlFor="missing-reports-search-filter">Search</label>
          <input
            id="missing-reports-search-filter"
            type="text"
            className="admin-filter-select"
            value={searchFilter}
            onChange={handleSearchChange}
            placeholder="Cari nama/email employee"
          />

          {isSuperadmin && (
            <>
              <label htmlFor="missing-reports-company-filter">Company</label>
              <select
                id="missing-reports-company-filter"
                className="admin-filter-select"
                value={companyFilter}
                onChange={handleCompanyChange}
              >
                <option value="">Semua Company</option>
                {companies.map((company) => (
                  <option key={company.company_id} value={company.company_id}>{company.name}</option>
                ))}
              </select>
            </>
          )}

          {(wilayahFilter || searchFilter || (isSuperadmin && companyFilter)) && (
            <button
              type="button"
              className="admin-btn-secondary"
              onClick={handleResetFilters}
            >
              Reset Filter
            </button>
          )}
        </div>
      </div>

      <p className="admin-page__meta">
        Total: {pagination.total} employee missing{selectedFilters.length ? ` (${selectedFilters.join(', ')})` : ''}
      </p>

      {items.length === 0 && !errorMessage ? (
        <EmptyState icon={'\u26A0'} title="Tidak ada missing reports" />
      ) : (
        items.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Employee</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Wilayah</th>
                  <th>Missing Reason</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={`${item.user_id || 'user'}-${item.period || 'period'}-${index}`}>
                    <td>{getPeriodLabel(item.period || periodFilter)}</td>
                    <td>{(item.name || '').trim() || `User #${item.user_id || '-'}`}</td>
                    <td>{(item.email || '').trim() || '-'}</td>
                    <td>{(item.company?.name || '').trim() || '-'}</td>
                    <td>{getDisplayWilayah(item.wilayah)}</td>
                    <td>{resolveMissingReasonLabel(item.missing_reason)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {pagination.last_page > 1 && (
        <div className="admin-pagination">
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={() => updateListSearchParams({ pageValue: pagination.page - 1 })}
          >
            {'< Prev'}
          </button>
          <span>Hal {pagination.page} / {pagination.last_page}</span>
          <button
            type="button"
            disabled={pagination.page >= pagination.last_page}
            onClick={() => updateListSearchParams({ pageValue: pagination.page + 1 })}
          >
            {'Next >'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MissingReportsPage;
